import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { HomeLanding } from "@/components/HomeLanding";
import { Layout } from "@/components/Layout";
import { RequireAuth } from "@/components/RequireAuth";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminAuditLogPage } from "@/pages/AdminAuditLogPage";
import { SettingsProfilePage } from "@/pages/SettingsProfilePage";
import { SettingsSecurityPage } from "@/pages/SettingsSecurityPage";
import { AdminConfigPage } from "@/pages/AdminConfigPage";
import AdminCredentialsPage from "@/pages/AdminCredentialsPage";
import RebootAgenciasPage from "@/pages/RebootAgenciasPage";
import ZoomPage from "@/pages/ZoomPage";
import { AdminSegmentsPage } from "@/pages/AdminSegmentsPage";
import { AdminTelegramPage } from "@/pages/AdminTelegramPage";
import { AdminUserDetailPage } from "@/pages/AdminUserDetailPage";
import { AdminUsersPage } from "@/pages/AdminUsersPage";
import { ConfigProfileDetailPage } from "@/pages/ConfigProfileDetailPage";
import { ControlConfiguracionPage } from "@/pages/ControlConfiguracionPage";
import { NewConfigProfilePage } from "@/pages/NewConfigProfilePage";
import { RollbacksHistoryPage } from "@/pages/RollbacksHistoryPage";
import { AdminUsagePage } from "@/pages/AdminUsagePage";
import { AnalysisPage } from "@/pages/AnalysisPage";
import { AnalysisSessionPage } from "@/pages/AnalysisSessionPage";
import { HablarConArocPage } from "@/pages/HablarConArocPage";
import { RecetasPage } from "@/pages/RecetasPage";
import { AuditoriaPlaticasPage } from "@/pages/AuditoriaPlaticasPage";
import { CorreoPage } from "@/pages/CorreoPage";
import { ApprovalsPage } from "@/pages/ApprovalsPage";
import { DashboardsPage } from "@/pages/DashboardsPage";
import { HolandaCarritoTableroPage } from "@/pages/HolandaCarritoTableroPage";
import { HealthPage } from "@/pages/HealthPage";
import { HolandaAgencyPage } from "@/pages/HolandaAgencyPage";
import { HolandaCarritosPage } from "@/pages/HolandaCarritosPage";
import { IncidentDetailPage } from "@/pages/IncidentDetailPage";
import { LoginPage } from "@/pages/LoginPage";
import { ProactiveAnalysisPage } from "@/pages/ProactiveAnalysisPage";
import { ECCPage } from "@/pages/ECCPage";
import { EccHubPage } from "@/pages/EccHubPage";
import { EccNewAnalysisPage } from "@/pages/EccNewAnalysisPage";
import { EccCatalogPage } from "@/pages/EccCatalogPage";
import { EccQueuePage } from "@/pages/EccQueuePage";
import { EccHistoryPage } from "@/pages/EccHistoryPage";
import { EccCallsPage } from "@/pages/EccCallsPage";
import { NewSRPPage } from "@/pages/NewSRPPage";
import { OperacionRespuestaPage } from "@/pages/OperacionRespuestaPage";
import { SrpCatalogPage } from "@/pages/SrpCatalogPage";
import { ReportsPage } from "@/pages/ReportsPage";
import { RunsPage } from "@/pages/RunsPage";
import { SecurityPage } from "@/pages/SecurityPage";
import { TotpForcedSetupPage } from "@/pages/TotpForcedSetupPage";
import { UsagePage } from "@/pages/UsagePage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Setup forzado vive fuera de RequireAuth: el operador llega con
                cookie scope=2fa_setup_pending, /me 403 → RequireAuth lo
                rebotaría a /login en loop. Tras enable, la cookie se
                promueve y se redirige al dashboard. */}
            <Route
              path="/account/security/setup-forced"
              element={<TotpForcedSetupPage />}
            />
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="account/security" element={<SecurityPage />} />
              <Route
                path="settings/profile"
                element={<SettingsProfilePage />}
              />
              <Route
                path="settings/security"
                element={<SettingsSecurityPage />}
              />
              {/* Hub "Operación de respuesta automática": layout pathless que
                  comparte el menú horizontal sobre estas vistas sin cambiar sus
                  rutas (Incidentes, Nuevo análisis, Aprobaciones, Historial de
                  Autorremediaciones, Catálogo de SRPs) + sus sub-vistas. */}
              <Route element={<OperacionRespuestaPage />}>
                <Route index element={<HomeLanding />} />
                <Route
                  path="incidents/:incidentId"
                  element={<IncidentDetailPage />}
                />
                <Route
                  path="incidents/:incidentId/analysis"
                  element={<AnalysisPage />}
                />
                <Route
                  path="analysis/new"
                  element={<ProactiveAnalysisPage />}
                />
                <Route
                  path="analysis/sessions/:sessionId"
                  element={<AnalysisSessionPage />}
                />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="runs" element={<RunsPage />} />
                <Route path="srp/catalog" element={<SrpCatalogPage />} />
              </Route>
              <Route path="hablar" element={<HablarConArocPage />} />
              <Route path="hablar/:sessionId" element={<HablarConArocPage />} />
              <Route path="reportes-programados" element={<RecetasPage />} />
              <Route path="recetas" element={<Navigate to="/reportes-programados" replace />} />
              <Route path="auditoria-platicas" element={<AuditoriaPlaticasPage />} />
              <Route path="correo" element={<CorreoPage />} />
              <Route path="health" element={<HealthPage />} />
              <Route
                path="holanda/carritos"
                element={<HolandaCarritosPage />}
              />
              <Route
                path="holanda/carritos/:agencyCode"
                element={<HolandaAgencyPage />}
              />
              <Route
                path="holanda/tablero-carrito"
                element={<HolandaCarritoTableroPage />}
              />
              <Route path="dashboards" element={<DashboardsPage />} />
              {/* Pantalla oculta de reinicio de agencias Holanda: fuera del
                  menu (no hay NavLink en Layout), solo se llega por esta URL,
                  enlazada desde el tablero de Grafana. Detras de RequireAuth.
                  Ver docs/decisions/AROC_Decisiones_Reboot_Manual_Agencias.md */}
              <Route path="reboot-agencias" element={<RebootAgenciasPage />} />
              <Route path="zoom" element={<ZoomPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="ecc" element={<ECCPage />}>
                <Route index element={<EccHubPage />} />
                <Route path="new" element={<EccNewAnalysisPage />} />
                <Route path="catalog" element={<EccCatalogPage />} />
                <Route path="queue" element={<EccQueuePage />} />
                <Route path="history" element={<EccHistoryPage />} />
                <Route path="calls" element={<EccCallsPage />} />
                <Route path="new-srp" element={<NewSRPPage />} />
              </Route>
              <Route path="usage" element={<UsagePage />} />
              <Route path="admin/usage" element={<AdminUsagePage />} />
              <Route path="admin/telegram" element={<AdminTelegramPage />} />
              <Route path="admin/users" element={<AdminUsersPage />} />
              <Route
                path="admin/users/:userId"
                element={<AdminUserDetailPage />}
              />
              <Route path="admin/segments" element={<AdminSegmentsPage />} />
              <Route path="admin/audit-log" element={<AdminAuditLogPage />} />
              <Route path="admin/configuracion" element={<AdminConfigPage />} />
              <Route
                path="admin/configuracion/control"
                element={<ControlConfiguracionPage />}
              />
              <Route
                path="admin/credenciales"
                element={<AdminCredentialsPage />}
              />
              <Route
                path="admin/configuracion/rollbacks"
                element={<RollbacksHistoryPage />}
              />
              <Route
                path="admin/configuracion/nuevo"
                element={<NewConfigProfilePage />}
              />
              <Route
                path="admin/configuracion/:profileId"
                element={<ConfigProfileDetailPage />}
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
